import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText,
  Container, Grid, Card, CardContent, Paper, Box, LinearProgress, Alert, Menu, MenuItem, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, InputLabel, FormControl,
  Divider, Tooltip, Tabs, Tab, Chip, Switch, FormControlLabel, Accordion, AccordionSummary,
  AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Badge, ListItemSecondaryAction, IconButton as MuiIconButton, Snackbar, CircularProgress,
  Stepper, Step, StepLabel, StepContent, CardActions, CardMedia, ListItemAvatar, useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Key as KeyIcon,
  Monitor as MonitorIcon,
  Code as CodeIcon,
  Public as PublicIcon,
  Article as ArticleIcon,
  Campaign as CampaignIcon,
  ContactMail as ContactMailIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ManageAccounts as ManageAccountsIcon,
  AccountCircle as AccountCircleIcon,
  Badge as BadgeIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Money as MoneyIcon,
  Assignment as AssignmentIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Update as UpdateIcon,
  Archive as ArchiveIcon,
  DeleteForever as DeleteForeverIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Create as CreateIcon,
  Send as SendIcon,
  Print as PrintIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Language as LanguageIcon,
  VpnKey as VpnKeyIcon,
  Fingerprint as FingerprintIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  Filter as FilterIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  BugReport as BugReportIcon,
  SupportAgent as SupportAgentIcon,
  Message as MessageIcon,
  Build as BuildIcon,
  Construction as ConstructionIcon,
  Engineering as EngineeringIcon,
  Science as ScienceIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  CloudSync as CloudSyncIcon,
  CloudOff as CloudOffIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  SignalWifi4Bar as SignalWifi4BarIcon,
  SignalWifiOff as SignalWifiOffIcon,
  Router as RouterIcon,
  NetworkCheck as NetworkCheckIcon,
  NetworkWifi as NetworkWifiIcon,
  NetworkCell as NetworkCellIcon,
  NetworkLocked as NetworkLockedIcon,
  NetworkPing as NetworkPingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../common/Logo';
import AdminAnalytics from './AdminAnalytics';
import NotificationCenter from './NotificationCenter';
import UserEditDialog from './UserEditDialog';
import EmailTemplateDialog from './EmailTemplateDialog';
import SystemSettingsDialog from './SystemSettingsDialog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AdminDashboardProps {}

interface WebsitePage {
  id: string;
  title: string;
  slug: string;
  content: string;
  status: string;
  lastModified: string;
}

interface WebsiteContent {
  homepage: {
    heroTitle: string;
    heroSubtitle: string;
    features: any[];
    testimonials: any[];
  };
  pages: WebsitePage[];
}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [userEditDialog, setUserEditDialog] = useState<{ open: boolean; user: any }>({ open: false, user: null });
  const [emailTemplateDialog, setEmailTemplateDialog] = useState<{ open: boolean; template: any }>({ open: false, template: null });
  const [systemSettingsDialog, setSystemSettingsDialog] = useState<{ open: boolean; settings: any }>({ open: false, settings: null });
  const navigate = useNavigate();

  // Real Data from Backend
  const [users, setUsers] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalPRs: 0,
    publishedPRs: 0,
    revenueMTD: 0,
    revenueYTD: 0,
    serverHealth: 'Operational',
    apiLatency: 'N/A',
    dbConnections: 0,
    uptime: 'N/A',
    storageUsed: 'N/A',
    bandwidthUsed: 'N/A',
    securityAlerts: 0,
    pendingTasks: 0,
    dbStatus: 'Connected',
    memoryUsed: 'N/A',
    memoryTotal: 'N/A'
  });

  // Placeholder data for features not yet implemented in backend
  const [websiteContent, setWebsiteContent] = useState<WebsiteContent>({
    homepage: {
      heroTitle: 'Transform Your PR Strategy with AI-Powered Distribution',
      heroSubtitle: 'Reach global audiences through intelligent channel selection and real-time analytics',
      features: [],
      testimonials: []
    },
    pages: []
  });

  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [securityLogs, setSecurityLogs] = useState<any[]>([]);
  const [backups, setBackups] = useState<any[]>([]);
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [ticketEditDialog, setTicketEditDialog] = useState<{ open: boolean; ticket: any }>({ open: false, ticket: null });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; user: any }>({ open: false, user: null });

  // Fetch all dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setDataLoading(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      
      if (!token) {
        showSnackbar('Authentication required', 'error');
        navigate('/admin');
        return;
      }

      // Validate token format - reject fake tokens
      if (token.startsWith('admin-token-')) {
        showSnackbar('Invalid authentication. Please login again.', 'error');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin');
        return;
      }

      // Fetch users, payments, and support tickets first
      const [usersResponse, paymentsResponse, ticketsResponse] = await Promise.allSettled([
        fetchUsers(token),
        fetchPayments(token),
        fetchSupportTickets(token)
      ]);

      // Handle users
      let fetchedUsers: any[] = [];
      if (usersResponse.status === 'fulfilled') {
        fetchedUsers = usersResponse.value;
        setUsers(fetchedUsers);
      } else {
        console.error('Error fetching users:', usersResponse.reason);
      }

      // Handle payments
      let fetchedPayments: any[] = [];
      if (paymentsResponse.status === 'fulfilled') {
        fetchedPayments = paymentsResponse.value;
        setPayments(fetchedPayments);
      } else {
        console.error('Error fetching payments:', paymentsResponse.reason);
      }

      // Handle support tickets
      let fetchedTickets: any[] = [];
      if (ticketsResponse.status === 'fulfilled') {
        fetchedTickets = ticketsResponse.value;
        setSupportTickets(fetchedTickets);
      } else {
        console.error('Error fetching support tickets:', ticketsResponse.reason);
      }

      // Fetch stats and system health (which may use the fetched data)
      const [statsResponse, healthResponse] = await Promise.allSettled([
        fetchSystemStats(token, fetchedUsers, fetchedPayments),
        fetchSystemHealth(token)
      ]);

      // Handle stats
      if (statsResponse.status === 'fulfilled') {
        const stats = statsResponse.value;
        // Merge health data if available
        if (healthResponse.status === 'fulfilled' && healthResponse.value) {
          setSystemStats({
            ...stats,
            ...healthResponse.value
          });
        } else {
          setSystemStats(stats);
        }
      } else {
        console.error('Error fetching stats:', statsResponse.reason);
      }

      // Handle health separately if stats failed
      if (healthResponse.status === 'fulfilled' && healthResponse.value) {
        setSystemStats(prev => ({
          ...prev,
          ...healthResponse.value
        }));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchUsers = async (token: string) => {
    try {
      // Try to fetch all users from admin endpoint
      // Note: This endpoint needs to be created in the backend
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.users) {
        return response.data.users.map((user: any) => ({
          id: user._id || user.id,
          _id: user._id || user.id,
          name: user.fullName || user.name,
          fullName: user.fullName || user.name,
          email: user.email,
          company: user.company || 'N/A',
          role: user.role || 'user',
          plan: user.plan || 'basic',
          status: user.isActive ? 'Active' : 'Inactive',
          isActive: user.isActive,
          createdAt: user.createdAt,
          lastLogin: user.lastLogin,
          permissions: user.permissions || []
        }));
      }
      return [];
    } catch (error: any) {
      // If endpoint doesn't exist (404 or other error), return empty array
      console.log('Admin users endpoint not available:', error.response?.status || error.message);
      return [];
    }
  };

  const fetchPayments = async (token: string) => {
    try {
      // Use admin payments endpoint
      const response = await axios.get(`${API_BASE_URL}/api/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 100
        }
      });
      
      if (response.data.success && response.data.payments) {
        return response.data.payments.map((payment: any) => ({
          id: payment._id || payment.id,
          userId: payment.userId?._id || payment.userId,
          userName: payment.userName || payment.userId?.fullName || 'Unknown User',
          userEmail: payment.userEmail || payment.userId?.email || 'N/A',
          amount: payment.amount || 0,
          currency: payment.currency || 'USD',
          status: payment.status || 'Pending',
          date: payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : payment.date || 'N/A',
          createdAt: payment.createdAt || payment.date,
          plan: payment.plan || 'N/A',
          method: payment.method || payment.paymentMethod || 'N/A',
          transactionId: payment.transactionId || payment._id || 'N/A'
        }));
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching payments:', error.response?.status || error.message);
      // Return empty array if endpoint doesn't exist or auth fails
      return [];
    }
  };

  const fetchSupportTickets = async (token: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/support/admin/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 100
        }
      });
      
      if (response.data.success && response.data.tickets) {
        return response.data.tickets.map((ticket: any) => ({
          _id: ticket._id,
          ticketId: ticket.ticketId,
          title: ticket.title,
          description: ticket.description,
          status: ticket.status,
          priority: ticket.priority,
          category: ticket.category,
          assignedTo: ticket.assignedTo,
          responses: ticket.responses || 0,
          createdAt: ticket.createdAt,
          updatedAt: ticket.updatedAt,
          userId: ticket.userId?._id || ticket.userId,
          userName: ticket.userId?.firstName && ticket.userId?.lastName 
            ? `${ticket.userId.firstName} ${ticket.userId.lastName}`
            : ticket.userId?.email || 'Unknown User',
          userEmail: ticket.userId?.email || 'N/A',
          userCompany: ticket.userId?.company || 'N/A'
        }));
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching support tickets:', error.response?.status || error.message);
      return [];
    }
  };

  const fetchSystemStats = async (token: string, users: any[] = [], payments: any[] = []) => {
    try {
      // Validate token format first
      if (!token || token.startsWith('admin-token-')) {
        console.warn('Invalid admin token format. Please login again.');
        return systemStats;
      }

      // Fetch system statistics from multiple endpoints
      const [usersStats, prStats, paymentStats] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/users/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.log('Users stats endpoint error:', err.response?.status || err.message);
          return null;
        }),
        axios.get(`${API_BASE_URL}/api/pr/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.log('PR stats endpoint error:', err.response?.status || err.message);
          return null;
        }),
        axios.get(`${API_BASE_URL}/api/payments/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((err) => {
          console.log('Payments stats endpoint error:', err.response?.status || err.message);
          return null;
        })
      ]);

      // Calculate stats from available data
      let totalUsers = 0;
      let activeUsers = 0;
      let totalPRs = 0;
      let publishedPRs = 0;
      let revenueMTD = 0;
      let revenueYTD = 0;

      // Get user stats
      if (usersStats.status === 'fulfilled' && usersStats.value?.data) {
        const userData = usersStats.value.data;
        totalUsers = userData.totalUsers || userData.stats?.totalUsers || 0;
        activeUsers = userData.activeUsers || userData.stats?.activeUsers || 0;
      }

      // Get PR stats - try admin endpoint or dashboard endpoint
      if (prStats.status === 'fulfilled' && prStats.value?.data) {
        const prData = prStats.value.data;
        totalPRs = prData.totalPRs || prData.stats?.totalPRs || 0;
        publishedPRs = prData.publishedPRs || prData.stats?.publishedPRs || 0;
      }

      // If PR stats endpoint doesn't exist, try dashboard endpoint
      if (totalPRs === 0) {
        try {
          const dashboardResponse = await axios.get(`${API_BASE_URL}/api/pr/analytics/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (dashboardResponse.data?.dashboard?.overview) {
            totalPRs = dashboardResponse.data.dashboard.overview.totalPRs || 0;
            publishedPRs = dashboardResponse.data.dashboard.overview.publishedPRs || 0;
          }
        } catch (err: any) {
          // If it's a 401, the token might be invalid
          if (err.response?.status === 401) {
            console.warn('Authentication failed. Token may be invalid or expired.');
          } else {
            console.log('Dashboard endpoint not available for PR stats');
          }
        }
      }

      // Get payment stats
      if (paymentStats.status === 'fulfilled' && paymentStats.value?.data) {
        const paymentData = paymentStats.value.data;
        revenueMTD = paymentData.revenueMTD || paymentData.stats?.revenueMTD || 0;
        revenueYTD = paymentData.revenueYTD || paymentData.stats?.revenueYTD || 0;
      }

      // If endpoints don't exist, calculate from fetched data
      if (totalUsers === 0 && users.length > 0) {
        totalUsers = users.length;
        activeUsers = users.filter((u: any) => u.status === 'Active' || u.isActive).length;
      }

      if (revenueMTD === 0 && payments.length > 0) {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        
        revenueMTD = payments
          .filter((p: any) => {
            try {
              // Handle both date string and Date object
              const paymentDate = p.createdAt ? new Date(p.createdAt) : (p.date ? new Date(p.date) : null);
              if (!paymentDate || isNaN(paymentDate.getTime())) return false;
              return paymentDate >= startOfMonth && (p.status === 'Completed' || p.status === 'completed');
            } catch {
              return false;
            }
          })
          .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
        
        revenueYTD = payments
          .filter((p: any) => {
            try {
              // Handle both date string and Date object
              const paymentDate = p.createdAt ? new Date(p.createdAt) : (p.date ? new Date(p.date) : null);
              if (!paymentDate || isNaN(paymentDate.getTime())) return false;
              return paymentDate >= startOfYear && (p.status === 'Completed' || p.status === 'completed');
            } catch {
              return false;
            }
          })
          .reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
      }

      return {
        totalUsers,
        activeUsers,
        totalPRs,
        publishedPRs,
        revenueMTD,
        revenueYTD,
        serverHealth: 'Operational',
        apiLatency: 'N/A',
        dbConnections: 0,
        uptime: 'N/A',
        storageUsed: 'N/A',
        bandwidthUsed: 'N/A',
        securityAlerts: 0,
        pendingTasks: 0,
        dbStatus: 'Connected',
        memoryUsed: 'N/A',
        memoryTotal: 'N/A'
      };
    } catch (error) {
      console.error('Error fetching system stats:', error);
      return systemStats;
    }
  };

  const fetchSystemHealth = async (token: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/system/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.health) {
        const health = response.data.health;
        return {
          serverHealth: health.serverHealth || 'Operational',
          apiLatency: health.apiLatency || 'N/A',
          uptime: health.uptime || 'N/A',
          storageUsed: health.storageUsed || 'N/A',
          dbConnections: health.dbConnections || 0,
          securityAlerts: health.securityAlerts || 0,
          dbStatus: health.dbStatus || 'Connected',
          memoryUsed: health.memoryUsed || 'N/A',
          memoryTotal: health.memoryTotal || 'N/A'
        };
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching system health:', error.response?.status || error.message);
      return null;
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Only remove admin-specific tokens, preserve regular user token if it exists
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    // Note: We intentionally do NOT remove 'token' here to preserve regular user session
    navigate('/admin');
  };

  const handleRefresh = () => {
    fetchDashboardData();
    showSnackbar('Data refreshed', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteUser = async (user: any) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      const response = await axios.delete(`${API_BASE_URL}/api/admin/users/${user.id || user._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showSnackbar('User and all related data deleted permanently', 'success');
        // Remove user from local state
        setUsers(users.filter((u: any) => (u.id || u._id) !== (user.id || user._id)));
        // Refresh dashboard data
        await fetchDashboardData();
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      showSnackbar(error.response?.data?.message || 'Failed to delete user', 'error');
    } finally {
      setLoading(false);
      setDeleteConfirmDialog({ open: false, user: null });
    }
  };

  const handleUpdateUserStatus = async (user: any) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        showSnackbar('Authentication required', 'error');
        return;
      }

      const newStatus = !(user.isActive || user.status === 'Active');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/users/${user.id || user._id}/status`,
        { isActive: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar(`User ${newStatus ? 'activated' : 'suspended'} successfully`, 'success');
        // Update user in local state
        setUsers(users.map((u: any) => 
          (u.id || u._id) === (user.id || user._id)
            ? { ...u, isActive: newStatus, status: newStatus ? 'Active' : 'Inactive' }
            : u
        ));
        // Refresh dashboard data
        await fetchDashboardData();
      }
    } catch (error: any) {
      console.error('Error updating user status:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update user status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => {
    if (dataLoading) {
      return (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
    <Grid container spacing={3}>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" style={{ color: '#000000', marginBottom: '20px' }}>System Overview</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={dataLoading}
            style={{ borderColor: '#000000', color: '#000000' }}
          >
            Refresh
          </Button>
      </Grid>
      
      {/* Key Metrics */}
      <Grid item xs={12} md={3}>
        <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <PeopleIcon style={{ color: '#FF6B35', marginRight: '10px' }} />
              <Typography variant="h6" style={{ color: '#000000' }}>Total Users</Typography>
            </Box>
            <Typography variant="h4" style={{ color: '#FF6B35', fontWeight: 'bold' }}>{systemStats.totalUsers}</Typography>
            <Typography variant="body2" style={{ color: '#666666' }}>{systemStats.activeUsers} active users</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <ArticleIcon style={{ color: '#0066CC', marginRight: '10px' }} />
              <Typography variant="h6" style={{ color: '#000000' }}>Active PRs</Typography>
            </Box>
            <Typography variant="h4" style={{ color: '#0066CC', fontWeight: 'bold' }}>{systemStats.totalPRs}</Typography>
            <Typography variant="body2" style={{ color: '#666666' }}>{systemStats.publishedPRs} published</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <MoneyIcon style={{ color: '#FF6B35', marginRight: '10px' }} />
              <Typography variant="h6" style={{ color: '#000000' }}>Revenue MTD</Typography>
            </Box>
            <Typography variant="h4" style={{ color: '#FF6B35', fontWeight: 'bold' }}>${systemStats.revenueMTD.toLocaleString()}</Typography>
            <Typography variant="body2" style={{ color: '#666666' }}>${systemStats.revenueYTD.toLocaleString()} YTD</Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={3}>
        <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <SecurityIcon style={{ color: '#000000', marginRight: '10px' }} />
              <Typography variant="h6" style={{ color: '#000000' }}>Security Alerts</Typography>
            </Box>
            <Typography variant="h4" style={{ color: '#000000', fontWeight: 'bold' }}>{systemStats.securityAlerts}</Typography>
            <Typography variant="body2" style={{ color: '#666666' }}>{systemStats.securityAlerts > 0 ? 'Requires attention' : 'All clear'}</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* System Health */}
      <Grid item xs={12}>
        <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '20px' }}>
          <CardContent>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <Typography variant="h6" style={{ color: '#000000', fontWeight: 'bold' }}>System Health</Typography>
              <Chip 
                label={systemStats.serverHealth} 
                size="small" 
                style={{ 
                  backgroundColor: systemStats.serverHealth === 'Operational' ? '#4caf50' : '#f44336',
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>Server Status</Typography>
                  <Typography variant="h6" style={{ color: systemStats.serverHealth === 'Operational' ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                    {systemStats.serverHealth}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    DB: {systemStats.dbStatus || 'Connected'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>API Latency</Typography>
                  <Typography variant="h6" style={{ color: '#0927eb', fontWeight: 'bold' }}>
                    {systemStats.apiLatency}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Response time
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>Uptime</Typography>
                  <Typography variant="h6" style={{ color: '#0927eb', fontWeight: 'bold' }}>
                    {systemStats.uptime}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Server runtime
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>Storage Used</Typography>
                  <Typography variant="h6" style={{ color: '#0927eb', fontWeight: 'bold' }}>
                    {systemStats.storageUsed}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Database size
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>Memory Usage</Typography>
                  <Typography variant="h6" style={{ color: '#0927eb', fontWeight: 'bold' }}>
                    {systemStats.memoryUsed}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    {systemStats.memoryTotal && systemStats.memoryTotal !== 'N/A' ? `of ${systemStats.memoryTotal}` : 'Heap memory'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: systemStats.securityAlerts > 0 ? '#fff3e0' : '#f5f5f5', borderRadius: '8px', border: systemStats.securityAlerts > 0 ? '1px solid #ff5200' : 'none' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>Security Alerts</Typography>
                  <Typography variant="h6" style={{ color: systemStats.securityAlerts > 0 ? '#f44336' : '#4caf50', fontWeight: 'bold' }}>
                    {systemStats.securityAlerts}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Last 24 hours
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666', marginBottom: '4px' }}>DB Connections</Typography>
                  <Typography variant="h6" style={{ color: '#0927eb', fontWeight: 'bold' }}>
                    {systemStats.dbConnections}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Active connections
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {systemStats.dbConnections > 0 && (
              <Box style={{ marginTop: '20px' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Typography variant="body2" style={{ color: '#666666' }}>Database Connection Pool</Typography>
                  <Typography variant="body2" style={{ color: '#666666' }}>
                    {systemStats.dbConnections} / 50
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((systemStats.dbConnections / 50) * 100, 100)}
                  style={{ height: '8px', borderRadius: '4px', backgroundColor: '#e0e0e0' }}
                  sx={{
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: systemStats.dbConnections > 40 ? '#f44336' : systemStats.dbConnections > 30 ? '#ff5200' : '#4caf50',
                      borderRadius: '4px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
  };

  const renderUserManagement = () => {
    if (dataLoading) {
      return (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
    <Box>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h4" style={{ color: '#000000' }}>User Management</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={dataLoading}
              style={{ borderColor: '#000000', color: '#000000', marginRight: '10px' }}
            >
              Refresh
            </Button>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          style={{ backgroundColor: '#000000', color: '#ffffff' }}
          onClick={() => showSnackbar('Add User dialog would open here')}
        >
          Add User
        </Button>
          </Box>
      </Box>
      
      <Paper style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '20px' }}>
          {users.length === 0 ? (
            <Box style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="body1" style={{ color: '#666666' }}>
                No users found. Admin API endpoint for fetching all users needs to be implemented.
              </Typography>
            </Box>
          ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>User</TableCell>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Company</TableCell>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Plan</TableCell>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Last Login</TableCell>
                <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                    <TableRow key={user.id || user._id}>
                  <TableCell>
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar style={{ marginRight: '10px', backgroundColor: '#0066CC' }}>
                            {(user.name || user.fullName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                      </Avatar>
                      <Box>
                            <Typography style={{ color: '#000000', fontWeight: 'bold' }}>{user.name || user.fullName || 'Unknown'}</Typography>
                            <Typography style={{ color: '#666666' }}>{user.email || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                      <TableCell style={{ color: '#666666' }}>{user.company || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip 
                          label={user.plan || 'N/A'} 
                      size="small" 
                      style={{ 
                            backgroundColor: user.plan === 'enterprise' || user.plan === 'Enterprise' ? '#FF6B35' : user.plan === 'premium' || user.plan === 'Premium' ? '#0066CC' : '#666666',
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                          label={user.status || (user.isActive ? 'Active' : 'Inactive')} 
                      size="small" 
                      style={{ 
                            backgroundColor: (user.status === 'Active' || user.isActive) ? '#0066CC' : '#FF6B35',
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  </TableCell>
                      <TableCell style={{ color: '#666666' }}>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                      </TableCell>
                  <TableCell>
                    <IconButton size="small" style={{ color: '#0066CC' }} onClick={() => setUserEditDialog({ open: true, user: user })}>
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      style={{ color: (user.status === 'Active' || user.isActive) ? '#FF6B35' : '#0066CC' }} 
                      onClick={() => handleUpdateUserStatus(user)}
                      disabled={loading}
                    >
                      {(user.status === 'Active' || user.isActive) ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                    <IconButton 
                      size="small" 
                      style={{ color: '#000000' }} 
                      onClick={() => setDeleteConfirmDialog({ open: true, user: user })}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          )}
      </Paper>
    </Box>
  );
  };

  const renderSupportTickets = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'open': return '#FF9800';
        case 'in_progress': return '#2196F3';
        case 'resolved': return '#4CAF50';
        case 'closed': return '#9E9E9E';
        default: return '#9E9E9E';
      }
    };

    const getPriorityColor = (priority: string) => {
      switch (priority) {
        case 'urgent': return '#F44336';
        case 'high': return '#FF5722';
        case 'medium': return '#FF9800';
        case 'low': return '#4CAF50';
        default: return '#9E9E9E';
      }
    };

    const handleUpdateTicket = async (ticketId: string, updates: any) => {
      try {
        setLoading(true);
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (!token) {
          showSnackbar('Authentication required', 'error');
          return;
        }
        const response = await axios.put(
          `${API_BASE_URL}/api/support/admin/tickets/${ticketId}`,
          updates,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          showSnackbar('Ticket updated successfully', 'success');
          const tickets = await fetchSupportTickets(token);
          setSupportTickets(tickets);
          setTicketEditDialog({ open: false, ticket: null });
        }
      } catch (error: any) {
        console.error('Error updating ticket:', error);
        showSnackbar(error.response?.data?.message || 'Failed to update ticket', 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <Box>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Typography variant="h4" style={{ color: '#000000' }}>Support Tickets</Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={async () => {
              const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
              if (token) {
                const tickets = await fetchSupportTickets(token);
                setSupportTickets(tickets);
              }
            }}
            disabled={dataLoading}
            style={{ borderColor: '#000000', color: '#000000' }}
          >
            Refresh
          </Button>
        </Box>
        <Paper style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {dataLoading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </Box>
          ) : supportTickets.length === 0 ? (
            <Box style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="body1" style={{ color: '#666666' }}>
                No support tickets found.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell style={{ fontWeight: 'bold' }}>Ticket ID</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>User</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Priority</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Created</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Responses</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supportTickets.map((ticket: any) => (
                    <TableRow key={ticket._id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {ticket.ticketId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {ticket.userName}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          {ticket.userEmail}
                        </Typography>
                        {ticket.userCompany && ticket.userCompany !== 'N/A' && (
                          <Typography variant="caption" style={{ color: '#999' }}>
                            {ticket.userCompany}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                          {ticket.title}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1).replace('_', ' ')}
                          style={{
                            backgroundColor: getStatusColor(ticket.status),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                          style={{
                            backgroundColor: getPriorityColor(ticket.priority),
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {ticket.category}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {ticket.assignedTo || 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Badge badgeContent={ticket.responses} color="primary">
                          <MessageIcon />
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View/Edit Ticket">
                          <IconButton 
                            size="small"
                            onClick={() => setTicketEditDialog({ open: true, ticket })}
                            style={{ color: '#0066CC' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Edit Ticket Dialog */}
        <Dialog 
          open={ticketEditDialog.open} 
          onClose={() => setTicketEditDialog({ open: false, ticket: null })} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" style={{ fontWeight: 'bold' }}>
              Edit Support Ticket
            </Typography>
            {ticketEditDialog.ticket && (
              <Typography variant="body2" style={{ color: '#666', marginTop: '8px' }}>
                {ticketEditDialog.ticket.ticketId} - {ticketEditDialog.ticket.userName}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {ticketEditDialog.ticket && (
              <Grid container spacing={3} style={{ marginTop: '8px' }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: '#666', marginBottom: '4px' }}>
                    Title
                  </Typography>
                  <Typography variant="body1" style={{ marginBottom: '16px' }}>
                    {ticketEditDialog.ticket.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: '#666', marginBottom: '4px' }}>
                    Description
                  </Typography>
                  <Typography variant="body1" style={{ marginBottom: '16px', whiteSpace: 'pre-wrap' }}>
                    {ticketEditDialog.ticket.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={ticketEditDialog.ticket.status}
                      onChange={(e) => {
                        const updatedTicket = { ...ticketEditDialog.ticket, status: e.target.value };
                        setTicketEditDialog({ open: true, ticket: updatedTicket });
                      }}
                      label="Status"
                    >
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={ticketEditDialog.ticket.priority}
                      onChange={(e) => {
                        const updatedTicket = { ...ticketEditDialog.ticket, priority: e.target.value };
                        setTicketEditDialog({ open: true, ticket: updatedTicket });
                      }}
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Assigned To"
                    value={ticketEditDialog.ticket.assignedTo || ''}
                    onChange={(e) => {
                      const updatedTicket = { ...ticketEditDialog.ticket, assignedTo: e.target.value };
                      setTicketEditDialog({ open: true, ticket: updatedTicket });
                    }}
                    placeholder="Enter assignee name or email"
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTicketEditDialog({ open: false, ticket: null })} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (ticketEditDialog.ticket) {
                  handleUpdateTicket(ticketEditDialog.ticket._id, {
                    status: ticketEditDialog.ticket.status,
                    priority: ticketEditDialog.ticket.priority,
                    assignedTo: ticketEditDialog.ticket.assignedTo
                  });
                }
              }}
              disabled={loading}
              style={{
                backgroundColor: '#000000',
                color: 'white'
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Update Ticket'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderContentManagement = () => (
    <Box>
      <Typography variant="h4" style={{ color: '#000000', marginBottom: '20px' }}>Content Management</Typography>
      
      <Grid container spacing={3}>
        {/* Homepage Content */}
        <Grid item xs={12} md={6}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#000000', marginBottom: '15px' }}>Homepage Content</Typography>
              <TextField
                fullWidth
                label="Hero Title"
                value={websiteContent.homepage.heroTitle}
                onChange={(e) => setWebsiteContent({...websiteContent, homepage: {...websiteContent.homepage, heroTitle: e.target.value}})}
                style={{ marginBottom: '15px' }}
                InputLabelProps={{ style: { color: '#666666' } }}
                InputProps={{ style: { color: '#000000' } }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Hero Subtitle"
                value={websiteContent.homepage.heroSubtitle}
                onChange={(e) => setWebsiteContent({...websiteContent, homepage: {...websiteContent.homepage, heroSubtitle: e.target.value}})}
                style={{ marginBottom: '15px' }}
                InputLabelProps={{ style: { color: '#666666' } }}
                InputProps={{ style: { color: '#000000' } }}
              />
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                style={{ backgroundColor: '#000000', color: '#ffffff' }}
                onClick={() => showSnackbar('Homepage content saved successfully')}
              >
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Pages Management */}
        <Grid item xs={12} md={6}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <Typography variant="h6" style={{ color: '#000000' }}>Pages</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  style={{ backgroundColor: '#000000', color: '#ffffff' }}
                  onClick={() => showSnackbar('Add page dialog would open')}
                >
                  Add Page
                </Button>
              </Box>
              {websiteContent.pages.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '20px' }}>
                  <Typography variant="body2" style={{ color: '#666666' }}>
                    No pages configured. Content management feature needs to be implemented.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {websiteContent.pages.map((page) => (
                    <ListItem key={page.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <ListItemText
                        primary={<Typography style={{ color: '#000000', fontWeight: 'bold' }}>{page.title}</Typography>}
                        secondary={
                          <Box>
                            <Typography style={{ color: '#666666' }}>/{page.slug}</Typography>
                            <Typography style={{ color: '#666666' }}>Last modified: {page.lastModified}</Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip 
                          label={page.status} 
                          size="small" 
                          style={{ 
                            backgroundColor: page.status === 'Published' ? '#0066CC' : '#FF6B35',
                            color: 'white',
                            fontWeight: 'bold',
                            marginRight: '10px'
                          }} 
                        />
                        <IconButton size="small" style={{ color: '#0066CC' }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" style={{ color: '#FF6B35' }}>
                          <VisibilityIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSecurityMonitoring = () => (
    <Box>
      <Typography variant="h4" style={{ color: '#000000', marginBottom: '20px' }}>Security Monitoring</Typography>
      
      <Grid container spacing={3}>
        {/* Security Overview */}
        <Grid item xs={12} md={4}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#000000', marginBottom: '15px' }}>Security Overview</Typography>
              <Box style={{ marginBottom: '10px' }}>
                <Typography variant="body1" style={{ color: '#000000' }}>Active Threats:</Typography>
                <Typography variant="h4" style={{ color: '#FF6B35' }}>{systemStats.securityAlerts}</Typography>
              </Box>
              <Box style={{ marginBottom: '10px' }}>
                <Typography variant="body1" style={{ color: '#000000' }}>Blocked IPs:</Typography>
                <Typography variant="h4" style={{ color: '#FF6B35' }}>N/A</Typography>
                <Typography variant="caption" style={{ color: '#666666' }}>Feature not implemented</Typography>
              </Box>
              <Box>
                <Typography variant="body1" style={{ color: '#000000' }}>Failed Logins (24h):</Typography>
                <Typography variant="h4" style={{ color: '#0066CC' }}>N/A</Typography>
                <Typography variant="caption" style={{ color: '#666666' }}>Feature not implemented</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Logs */}
        <Grid item xs={12} md={8}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#000000', marginBottom: '15px' }}>Recent Security Events</Typography>
              {securityLogs.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="body1" style={{ color: '#666666', marginBottom: '10px' }}>
                    No security logs available
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Security logging feature needs to be implemented in the backend
                  </Typography>
                </Box>
              ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>User/IP</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Timestamp</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {securityLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Chip 
                            label={log.type} 
                            size="small" 
                            style={{ 
                              backgroundColor: log.status === 'Success' ? '#0066CC' : '#FF6B35',
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell style={{ color: '#666666' }}>{log.user}</TableCell>
                        <TableCell style={{ color: '#666666' }}>{log.timestamp}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.status} 
                            size="small" 
                            style={{ 
                              backgroundColor: log.status === 'Success' ? '#0066CC' : '#FF6B35',
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell style={{ color: '#666666' }}>{log.details}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSystemSettings = () => (
    <Box>
      <Typography variant="h4" style={{ color: '#000000', marginBottom: '20px' }}>System Settings</Typography>
      
      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" style={{ color: '#000000', marginBottom: '15px' }}>General Settings</Typography>
              
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography style={{ color: '#000000' }}>Maintenance Mode</Typography>}
                style={{ marginBottom: '15px' }}
              />
              
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography style={{ color: '#000000' }}>Email Notifications</Typography>}
                style={{ marginBottom: '15px' }}
              />
              
              <FormControlLabel
                control={<Switch />}
                label={<Typography style={{ color: '#000000' }}>Debug Mode</Typography>}
                style={{ marginBottom: '15px' }}
              />
              
              <FormControlLabel
                control={<Switch defaultChecked />}
                label={<Typography style={{ color: '#000000' }}>Auto Backup</Typography>}
                style={{ marginBottom: '15px' }}
              />
              
              <Button
                variant="contained"
                style={{ backgroundColor: '#000000', color: '#ffffff', marginTop: '10px' }}
                onClick={() => showSnackbar('Settings saved successfully')}
              >
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* API Management */}
        <Grid item xs={12} md={6}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <Typography variant="h6" style={{ color: '#000000' }}>API Keys</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  style={{ backgroundColor: '#000000', color: '#ffffff' }}
                  onClick={() => showSnackbar('Generate new API key dialog would open')}
                >
                  Generate Key
                </Button>
              </Box>
              
              {apiKeys.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '20px' }}>
                  <Typography variant="body2" style={{ color: '#666666' }}>
                    No API keys configured. API key management feature needs to be implemented.
                  </Typography>
                </Box>
              ) : (
              <List>
                {apiKeys.map((apiKey) => (
                  <ListItem key={apiKey.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <ListItemText
                      primary={<Typography style={{ color: '#000000', fontWeight: 'bold' }}>{apiKey.name}</Typography>}
                      secondary={
                        <Box>
                          <Typography style={{ color: '#666666' }}>{apiKey.key}</Typography>
                          <Typography style={{ color: '#666666' }}>Last used: {apiKey.lastUsed}</Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip 
                        label={apiKey.status} 
                        size="small" 
                        style={{ 
                          backgroundColor: apiKey.status === 'Active' ? '#0066CC' : '#FF6B35',
                          color: 'white',
                          fontWeight: 'bold',
                          marginRight: '10px'
                        }} 
                      />
                      <IconButton size="small" style={{ color: '#0066CC' }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" style={{ color: '#FF6B35' }}>
                        <VisibilityIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Backup Management */}
        <Grid item xs={12}>
          <Card style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <Typography variant="h6" style={{ color: '#000000' }}>Backup Management</Typography>
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<BackupIcon />}
                    style={{ backgroundColor: '#000000', color: '#ffffff', marginRight: '10px' }}
                    onClick={() => showSnackbar('Backup feature needs to be implemented', 'info')}
                  >
                    Create Backup
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RestoreIcon />}
                    style={{ backgroundColor: '#0066CC', color: '#ffffff' }}
                    onClick={() => showSnackbar('Restore feature needs to be implemented', 'info')}
                  >
                    Restore
                  </Button>
                </Box>
              </Box>
              
              {backups.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="body1" style={{ color: '#666666', marginBottom: '10px' }}>
                    No backups available
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666666' }}>
                    Backup management feature needs to be implemented in the backend
                  </Typography>
                </Box>
              ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Backup Name</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Size</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Created</TableCell>
                      <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.id}>
                        <TableCell style={{ color: '#000000', fontWeight: 'bold' }}>{backup.name}</TableCell>
                        <TableCell style={{ color: '#666666' }}>{backup.size}</TableCell>
                        <TableCell>
                          <Chip 
                            label={backup.type} 
                            size="small" 
                            style={{ 
                              backgroundColor: '#0066CC',
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={backup.status} 
                            size="small" 
                            style={{ 
                              backgroundColor: '#0066CC',
                              color: 'white',
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell style={{ color: '#666666' }}>{backup.createdAt}</TableCell>
                        <TableCell>
                          <IconButton size="small" style={{ color: '#0066CC' }}>
                            <DownloadIcon />
                          </IconButton>
                          <IconButton size="small" style={{ color: '#FF6B35' }}>
                            <RestoreIcon />
                          </IconButton>
                          <IconButton size="small" style={{ color: '#000000' }}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const theme = useTheme();
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_BASE_URL}/api/notifications/user?page=1&limit=1&unreadOnly=true`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            setNotificationCount(response.data.pagination?.total || 0);
          }
        }
      } catch (err) {
        console.error('Failed to fetch notification count:', err);
      }
    };
    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh system health every 30 seconds
  useEffect(() => {
    const refreshSystemHealth = async () => {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token && !token.startsWith('admin-token-')) {
        const healthData = await fetchSystemHealth(token);
        if (healthData) {
          setSystemStats(prev => ({
            ...prev,
            ...healthData
          }));
        }
      }
    };
    
    // Initial fetch
    refreshSystemHealth();
    
    // Set up interval for auto-refresh
    const interval = setInterval(refreshSystemHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <Box style={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* App Bar - Enhanced Aesthetic Design */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar sx={{ 
          minHeight: '72px !important',
          height: 72,
          px: { xs: 2, sm: 3 },
          justifyContent: 'space-between'
        }}>
          {/* Left Section: Logo & Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Logo size="inline" />
              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}
                >
                  Admin Panel
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    lineHeight: 1,
                    mt: 0.25
                  }}
                >
                  Control Center
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right Section: Actions & Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Refresh Button */}
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={handleRefresh}
                disabled={dataLoading}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleNotificationMenuOpen}
                sx={{
                  color: theme.palette.text.secondary,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  }
                }}
              >
                <Badge badgeContent={notificationCount} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={handleNotificationMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 320,
                  maxHeight: 400,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  borderRadius: 2,
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {notificationCount} unread
                </Typography>
              </Box>
              <MenuItem 
                onClick={() => {
                  handleNotificationMenuClose();
                  setActiveTab('notifications');
                }}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <NotificationsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="View All Notifications" />
              </MenuItem>
            </Menu>

            {/* Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 32 }} />

            {/* Admin Profile Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                <AdminPanelSettingsIcon />
              </Avatar>
              <Button
                onClick={handleMenuOpen}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                Admin
              </Button>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  borderRadius: 2,
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Admin Account
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  System Administrator
                </Typography>
              </Box>
              <MenuItem onClick={handleMenuClose} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </MenuItem>
              <Divider />
              <MenuItem 
                onClick={handleLogout} 
                sx={{ 
                  py: 1.5,
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.light + '15',
                  }
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <Logo size="inline" />
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
              Admin
            </Typography>
          </Box>
        </Toolbar>
        <Box sx={{ overflow: 'auto' }}>
          <List>
            <ListItem 
              button 
              onClick={() => { setActiveTab('overview'); handleDrawerToggle(); }}
              selected={activeTab === 'overview'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <DashboardIcon sx={{ color: activeTab === 'overview' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText 
                primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'overview' ? 600 : 400 }}>Overview</Typography>} 
              />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('users'); handleDrawerToggle(); }}
              selected={activeTab === 'users'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <PeopleIcon sx={{ color: activeTab === 'users' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'users' ? 600 : 400 }}>User Management</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('payments'); handleDrawerToggle(); }}
              selected={activeTab === 'payments'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <PaymentIcon sx={{ color: activeTab === 'payments' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'payments' ? 600 : 400 }}>Payment Management</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('content'); handleDrawerToggle(); }}
              selected={activeTab === 'content'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <ArticleIcon sx={{ color: activeTab === 'content' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'content' ? 600 : 400 }}>Content Management</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('security'); handleDrawerToggle(); }}
              selected={activeTab === 'security'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <SecurityIcon sx={{ color: activeTab === 'security' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'security' ? 600 : 400 }}>Security Monitoring</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('analytics'); handleDrawerToggle(); }}
              selected={activeTab === 'analytics'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <AnalyticsIcon sx={{ color: activeTab === 'analytics' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'analytics' ? 600 : 400 }}>Analytics</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('notifications'); handleDrawerToggle(); }}
              selected={activeTab === 'notifications'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <Badge badgeContent={notificationCount} color="error" max={99}>
                  <NotificationsIcon sx={{ color: activeTab === 'notifications' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'notifications' ? 600 : 400 }}>Notifications</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('support'); handleDrawerToggle(); }}
              selected={activeTab === 'support'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <Badge badgeContent={supportTickets.filter((t: any) => t.status === 'open' || t.status === 'in_progress').length} color="error" max={99}>
                  <SupportAgentIcon sx={{ color: activeTab === 'support' ? theme.palette.primary.main : theme.palette.text.secondary }} />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'support' ? 600 : 400 }}>Support Tickets</Typography>} />
            </ListItem>
            <ListItem 
              button 
              onClick={() => { setActiveTab('settings'); handleDrawerToggle(); }}
              selected={activeTab === 'settings'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '15',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              <ListItemIcon>
                <SettingsIcon sx={{ color: activeTab === 'settings' ? theme.palette.primary.main : theme.palette.text.secondary }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.text.primary, fontWeight: activeTab === 'settings' ? 600 : 400 }}>System Settings</Typography>} />
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem 
              button 
              onClick={handleLogout}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.error.light + '15',
                }
              }}
            >
              <ListItemIcon>
                <LogoutIcon sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText primary={<Typography sx={{ color: theme.palette.error.main, fontWeight: 500 }}>Logout</Typography>} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box style={{ flex: 1, padding: '30px', marginTop: '72px', backgroundColor: theme.palette.background.default }}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUserManagement()}
        {activeTab === 'payments' && (
          <Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <Typography variant="h4" style={{ color: '#000000' }}>Payment Management</Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={dataLoading}
                style={{ borderColor: '#000000', color: '#000000' }}
              >
                Refresh
              </Button>
            </Box>
            <Paper style={{ borderRadius: '12px', backgroundColor: '#ffffff', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '20px' }}>
              {dataLoading ? (
                <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <CircularProgress />
                </Box>
              ) : payments.length === 0 ? (
                <Box style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="body1" style={{ color: '#666666' }}>
                    No payments found.
                  </Typography>
                </Box>
              ) : (
              <List>
                {payments.map((payment) => (
                  <ListItem key={payment.id} style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '10px', marginBottom: '10px' }}>
                    <ListItemText
                        primary={<Typography style={{ color: '#000000', fontWeight: 'bold' }}>{payment.userName} - ${payment.amount.toLocaleString()} {payment.currency}</Typography>}
                      secondary={
                        <Box>
                          <Typography style={{ color: '#666666' }}>Plan: {payment.plan} | Status: {payment.status}</Typography>
                          <Typography style={{ color: '#666666' }}>Date: {payment.date} | Method: {payment.method}</Typography>
                            {payment.transactionId && payment.transactionId !== 'N/A' && (
                              <Typography style={{ color: '#666666', fontSize: '0.75rem' }}>Transaction ID: {payment.transactionId}</Typography>
                            )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" style={{ color: '#0066CC' }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" style={{ color: '#FF6B35' }}>
                        <ReceiptIcon />
                      </IconButton>
                      <IconButton size="small" style={{ color: '#000000' }}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              )}
            </Paper>
          </Box>
        )}
        {activeTab === 'content' && renderContentManagement()}
        {activeTab === 'security' && renderSecurityMonitoring()}
        {activeTab === 'analytics' && <AdminAnalytics />}
        {activeTab === 'notifications' && <NotificationCenter />}
        {activeTab === 'support' && renderSupportTickets()}
        {activeTab === 'settings' && renderSystemSettings()}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog Components */}
      <UserEditDialog
        open={userEditDialog.open}
        onClose={() => setUserEditDialog({ open: false, user: null })}
        user={userEditDialog.user}
        onSave={(userData) => {
          showSnackbar('User updated successfully');
          setUserEditDialog({ open: false, user: null });
        }}
      />

      <EmailTemplateDialog
        open={emailTemplateDialog.open}
        onClose={() => setEmailTemplateDialog({ open: false, template: null })}
        template={emailTemplateDialog.template}
        onSave={(templateData) => {
          showSnackbar('Email template saved successfully');
          setEmailTemplateDialog({ open: false, template: null });
        }}
      />

      <SystemSettingsDialog
        open={systemSettingsDialog.open}
        onClose={() => setSystemSettingsDialog({ open: false, settings: null })}
        settings={systemSettingsDialog.settings}
        onSave={(settingsData) => {
          showSnackbar('System settings saved successfully');
          setSystemSettingsDialog({ open: false, settings: null });
        }}
      />

      {/* Delete User Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmDialog.open} 
        onClose={() => setDeleteConfirmDialog({ open: false, user: null })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#000000' }}>
            Confirm Permanent Deletion
          </Typography>
        </DialogTitle>
        <DialogContent>
          {deleteConfirmDialog.user && (
            <Box>
              <Alert severity="error" style={{ marginBottom: '20px' }}>
                <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                  Warning: This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  Deleting this user will permanently remove:
                </Typography>
                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  <li>User account (username, password, email)</li>
                  <li>All press releases</li>
                  <li>All payments and transactions</li>
                  <li>All campaigns</li>
                  <li>All distributions</li>
                  <li>All reports and analytics</li>
                  <li>All integrations</li>
                  <li>All media assets</li>
                  <li>All team members</li>
                  <li>All notifications</li>
                  <li>All support tickets</li>
                  <li>All other related data</li>
                </ul>
              </Alert>
              <Typography variant="body1" style={{ marginTop: '20px', color: '#000000' }}>
                Are you sure you want to permanently delete <strong>{deleteConfirmDialog.user.name || deleteConfirmDialog.user.fullName || deleteConfirmDialog.user.email}</strong>?
              </Typography>
              <Typography variant="body2" style={{ color: '#666666', marginTop: '10px' }}>
                Email: {deleteConfirmDialog.user.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false, user: null })} 
            disabled={loading}
            style={{ color: '#666666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => deleteConfirmDialog.user && handleDeleteUser(deleteConfirmDialog.user)}
            disabled={loading}
            style={{
              backgroundColor: '#f44336',
              color: 'white'
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;